import { PortableText, type PortableTextBlock } from '@portabletext/react'
import { SanityImage } from '#app/components/ui/SanityImage.tsx'
import { cn } from '#app/utils/misc.tsx'

export type ResolvedDoc = {
	title?: string
	subtitle?: string
	logo?: {
		asset?: {
			_id: string
			metadata?: {
				dimensions: { width: number; height: number; aspectRatio: number }
			}
		}
		alt?: string
		hotspot?: unknown
		crop?: unknown
	}
	body?: PortableTextBlock[]
}

const assetFragment = `asset->{ _id, metadata { dimensions { width, height, aspectRatio } } }`

export const generalDocumentQuery = `{
	title,
	subtitle,
	logo { ..., ${assetFragment} },
	body[] {
		...,
		"embeddedDoc": select(
			_type == "reference" => @->{ title, "body": body[]{ ..., ${assetFragment} } }
		),
		${assetFragment}
	}
}`

// Splices embedded document titles + bodies in place of reference blocks
function resolveBody(body: PortableTextBlock[]): PortableTextBlock[] {
	return body.flatMap((block) => {
		const b = block as unknown as Record<string, unknown>
		const embedded = b.embeddedDoc as
			| { title?: string; body?: PortableTextBlock[] }
			| undefined
		if (b._type === 'reference' && embedded) {
			const titleBlock: PortableTextBlock[] = embedded.title
				? [
						{
							_type: 'block',
							_key: `${String(b._key)}-title`,
							style: 'h3',
							markDefs: [],
							children: [{ _type: 'span', _key: 'span', text: embedded.title }],
						} as unknown as PortableTextBlock,
					]
				: []
			return [...titleBlock, ...(embedded.body ?? [])]
		}
		return [block]
	})
}

export function GeneralDocument({
	doc,
	className,
}: {
	doc: ResolvedDoc | null
	className?: string
}) {
	if (!doc) {
		return <p className="text-muted-foreground px-4">Coming soon.</p>
	}

	const body = doc.body ? resolveBody(doc.body) : null

	return (
		<article className={cn('mx-auto max-w-2xl px-4', className)}>
			{doc.logo?.asset ? (
				<div className="mb-6 flex justify-center">
					<SanityImage
						source={doc.logo as Parameters<typeof SanityImage>[0]['source']}
						alt={doc.logo.alt ?? ''}
						width={400}
						className="w-1/2 rounded-lg"
					/>
				</div>
			) : null}
			{doc.title ? (
				<h2 className="text-scbg-gold mb-1 text-center text-3xl font-bold">
					{doc.title}
				</h2>
			) : null}
			{doc.subtitle ? (
				<p className="mb-8 text-center text-slate-500 dark:text-slate-400">
					{doc.subtitle}
				</p>
			) : null}
			{body ? (
				<div
					className={cn(
						'prose prose-slate dark:prose-invert max-w-none',
						// When nothing renders above the body (no logo/title/subtitle),
						// shrink the leading heading's top margin so there's no empty
						// gap where the title would have been.
						!doc.logo?.asset &&
							!doc.title &&
							!doc.subtitle &&
							'[&>*:first-child]:mt-1',
					)}
				>
					<PortableText
						value={body}
						components={{
							block: {
								h2: ({ children }) => (
									<h2 className="text-scbg-gold border-scbg-gold mt-8 mb-3 border-b pb-2 text-3xl font-bold">
										{children}
									</h2>
								),
								h3: ({ children }) => (
									<h3 className="text-scbg-gold mt-6 mb-2 text-2xl font-bold">
										{children}
									</h3>
								),
								h4: ({ children }) => (
									<h4 className="mt-4 mb-1 text-xl font-bold text-slate-300">
										{children}
									</h4>
								),
							},
							marks: {
								link: ({ children, value }) => (
									<a
										href={(value as { href?: string }).href}
										target="_blank"
										rel="noopener noreferrer"
									>
										{children}
									</a>
								),
							},
							types: {
								image: ({ value }) => {
									const img = value as {
										alt?: string
										caption?: string
										asset?: unknown
									}
									if (!img.asset) return null
									return (
										<figure>
											<SanityImage
												source={
													value as Parameters<typeof SanityImage>[0]['source']
												}
												alt={img.alt ?? ''}
												className="rounded-lg"
											/>
											{img.caption ? (
												<figcaption className="mt-1 text-center text-sm text-slate-500 dark:text-slate-400">
													{img.caption}
												</figcaption>
											) : null}
										</figure>
									)
								},
							},
						}}
					/>
				</div>
			) : null}
		</article>
	)
}
